import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  FileText,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  FolderOpen,
  ListTree,
  NotebookTabs,
  MessageSquareText,
  Sparkles,
  Gavel,
  Compass,
  PenLine,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FilingsSection, type NPRMTimeline as FilingsNPRMTimeline } from '../components/proceedings/FilingsSection';
import {
  ProceedingDetailHeader,
  type PrevailingInstrument,
} from '../components/proceedings/ProceedingDetailHeader';
import { SummariesSection } from '../components/proceedings/SummariesSection';
import { ChatSection } from '../components/proceedings/ChatSection';
import { OrderSection } from '../components/proceedings/OrderSection';
import { NPRMPreviewSection } from '../components/proceedings/NPRMPreviewSection';
import { PublicNoticePreviewSection } from '../components/proceedings/PublicNoticePreviewSection';
import { TimelineSection } from '../components/proceedings/TimelineSection';
import { uploadFilingsParallel, type MinimalFiling } from '../lib/parallelFilingUpload';
import { fetchProceedingsFilings } from '../lib/proceedingsFilingRetrieval';
import { formatDeadlineCalendarDate } from '../utils/nprmHelpers';
import { useDocketMetadata } from '../hooks/useDocketMetadata';
import * as Sentry from '@sentry/react';

type ProceedingTab =
  | 'overview'
  | 'nprm_questions'
  | 'public_notice_questions'
  | 'filings'
  | 'comment_summaries'
  | 'draft_reply'
  | 'order'
  | 'chat';

const VALID_TABS: ProceedingTab[] = [
  'overview',
  'nprm_questions',
  'public_notice_questions',
  'filings',
  'comment_summaries',
  'draft_reply',
  'order',
  // 'chat' is intentionally omitted: the Comment Chat tab is still in
  // development and hidden from the tab bar, but leaving it here let anyone
  // reach the unfinished panel (with an unescaped-markdown XSS + several
  // hang states) via ?tab=chat. Re-add when the surface is hardened.
];

function parseTabParam(raw: string | null): ProceedingTab {
  return raw && (VALID_TABS as string[]).includes(raw) ? (raw as ProceedingTab) : 'overview';
}

export const ProceedingDetailsPage: React.FC = () => {
  const { docketNumber } = useParams<{ docketNumber: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading,
    error,
    proceeding,
    fileCount,
    nonExpressFilingCount,
    fetchingFilingCount,
    nprmCount,
    nprmDates,
    fetchingNprmCount,
    publicNoticeCount,
    publicNoticeDates,
    publicNoticeDocuments,
    docketDataResolved,
    commentDeadline,
    replyCommentDeadline,
    commentDeadlineExtension,
    replyDeadlineExtension,
    fetchingDeadlines,
    prevailingInstrument,
    uploadCycleStartDate,
    topPublicNoticePdfUrl,
    syntheticPnTimelineForFilings,
    refreshProceeding,
  } = useDocketMetadata(docketNumber);

  const [activeTab, setActiveTab] = useState<ProceedingTab>(() =>
    parseTabParam(searchParams.get('tab'))
  );

  // Keep state ↔ URL in sync. The URL drives the source of truth so deep
  // links (e.g. /proceedings/26-54?tab=comment_summaries from the
  // top-level "Comment Summaries" entry) land directly on the right tab.
  const handleTabChange = useCallback(
    (tab: ProceedingTab) => {
      setActiveTab(tab);
      const next = new URLSearchParams(searchParams);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // If the URL changes from elsewhere (browser nav, in-app link), follow it.
  useEffect(() => {
    const fromUrl = parseTabParam(searchParams.get('tab'));
    if (fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps


  // Auto-enroll + parallel upload state (owned by this page; the standalone
  // Comment Summaries page intentionally does not auto-enroll).
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [uploadStatusNote, setUploadStatusNote] = useState<string | null>(null);
  // The browser-driven ingest/upload pass runs for every visitor, but its raw
  // status ("Waiting for docket metadata before upload…", "Uploading PDFs…") is
  // internal pipeline vocabulary that shouldn't surface to regular users. Show
  // those banners to admins only; ingest still runs silently for everyone else.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let mounted = true;
    const ADMIN_EMAILS = ['nitintangellamudi@gmail.com'];
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setIsAdmin(!!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));
    }).catch(() => { if (mounted) setIsAdmin(false); });
    return () => { mounted = false; };
  }, []);
  const enrolledRef = useRef<string | null>(null);

  // Sync processing status based on uploaded documents
  const syncProcessingStatus = useCallback(async () => {
    if (!docketNumber || !proceeding) return;

    // Only sync if status is 'pending' (don't overwrite 'processing' or 'failed')
    if (proceeding.processing_status !== 'pending') {
      return;
    }

    try {
      // Query uploaded_documents table for this docket
      const { data: uploadedDocs, error: docsError } = await supabase
        .from('uploaded_documents')
        .select('uploaded_at')
        .eq('docket_number', docketNumber);

      if (docsError) {
        console.error('Error checking uploaded documents:', docsError);
        return;
      }

      // If documents exist, update processing status
      if (uploadedDocs && uploadedDocs.length > 0) {
        // Find most recent upload date
        const mostRecentUpload = uploadedDocs
          .map(doc => new Date(doc.uploaded_at))
          .sort((a, b) => b.getTime() - a.getTime())[0];

        // Update open_proceedings table
        const { error: updateError } = await supabase
          .from('open_proceedings')
          .update({
            processing_status: 'completed',
            last_processed_at: mostRecentUpload.toISOString(),
            last_document_count: uploadedDocs.length,
            updated_at: new Date().toISOString()
          })
          .eq('docket_number', docketNumber);

        if (updateError) {
          console.error('Error updating processing status:', updateError);
          return;
        }

        // Refresh proceeding details to reflect the update
        await refreshProceeding();
      }
    } catch (err) {
      console.error('Error syncing processing status:', err);
    }
  }, [docketNumber, proceeding, refreshProceeding]);


  // Sync processing status after proceeding details are loaded.
  // (Proceeding fetch, docket-data, deadlines, and PN-follow-up inference
  // are all owned by useDocketMetadata — this page no longer wires them.)
  useEffect(() => {
    if (proceeding && !loading) {
      syncProcessingStatus();
    }
  }, [proceeding, loading, syncProcessingStatus]);

  // Auto-enroll docket into ingest pipeline + trigger parallel PDF upload
  useEffect(() => {
    if (!docketNumber || enrolledRef.current === docketNumber) return;
    if (!docketDataResolved) {
      setUploadStatusNote('Waiting for docket metadata before upload...');
      return;
    }

    const hasInstrument = (nprmCount ?? 0) > 0 || (publicNoticeCount ?? 0) > 0;
    if (hasInstrument && !uploadCycleStartDate) {
      setUploadStatusNote('Waiting for cycle start date before upload...');
      return;
    }

    // Skip the upload pass if we already ran it for this docket in the current
    // browser session. enrolledRef only survives the component lifetime; without
    // this session-level guard the full upload pass (enroll API + per-filing
    // storage checks) fires on every navigation to the proceedings page.
    const sessionKey = `upload-checked:${docketNumber}`;
    if (sessionStorage.getItem(sessionKey)) {
      enrolledRef.current = docketNumber; // keep in-memory guard consistent
      // Clear any stale waiting note left by an earlier render where
      // docketDataResolved was still false.
      setUploadStatusNote(null);
      return;
    }

    enrolledRef.current = docketNumber;

    (async () => {
      // Pre-warm the NPRM questions cache by listing the NPRM folder and
      // firing a fire-and-forget request to /nprm_questions. Backend either
      // returns the cached payload (fast) or schedules extraction and
      // returns 'processing' (writes the cache file in the background).
      // Either way, the user clicking the NPRM tab later will see a warm
      // cache. Idempotent — safe to call on every page load.
      const prewarmNprmCache = async () => {
        try {
          if ((nprmCount ?? 0) <= 0) return;
          const { data: nprmFiles } = await supabase.storage
            .from('documents')
            .list(`ecfs/${docketNumber}/NPRM`, { limit: 1000 });
          const pdf = (nprmFiles || []).find((f: { name?: string }) => f.name?.toLowerCase().endsWith('.pdf'));
          if (!pdf?.name) return;
          const url = `https://fcc-summarize-app-r4ac3.ondigitalocean.app/nprm_questions?` +
            new URLSearchParams({
              docket_number: docketNumber,
              nprm_filename: pdf.name,
              method: 'llm',
            }).toString();
          fetch(url, { method: 'GET' }).catch((warmErr) => {
            console.warn('[proceedings-upload] NPRM cache pre-warm failed:', warmErr);
          });
          console.info(`[proceedings-upload] NPRM cache pre-warm dispatched for ${pdf.name}`);
        } catch (warmErr) {
          console.warn('[proceedings-upload] NPRM cache pre-warm scheduling error:', warmErr);
        }
      };

      try {
        setUploadStatusNote(null);

        // Server-side guard: if a previous session already completed the
        // verification pass for this docket, skip it entirely. The frontend
        // marks dockets.documents_verified_at after a successful pass; once
        // set, every subsequent visitor (any tab, any device) bypasses the
        // per-filing storage existence check that otherwise blocks the UI
        // for up to a minute.
        try {
          const { data: docketRow } = await supabase
            .from('dockets')
            .select('documents_verified_at')
            .eq('docket_number', docketNumber)
            .maybeSingle();
          if (docketRow?.documents_verified_at) {
            sessionStorage.setItem(sessionKey, '1');
            setUploadStatusNote(null);
            // Verification was already done — pre-warm NPRM cache for this visit.
            prewarmNprmCache();
            return;
          }
        } catch (verifyCheckErr) {
          console.warn('[proceedings-upload] documents_verified_at check failed; continuing with full pass:', verifyCheckErr);
        }

        const summarizeDates = (rows: Array<{ date_received?: string | null; date_submission?: string | null }>) => {
          const timestamps = rows
            .map((r) => Date.parse(r.date_received || r.date_submission || ''))
            .filter((ts) => !Number.isNaN(ts));
          if (timestamps.length === 0) {
            return { min: null as string | null, max: null as string | null };
          }
          const min = new Date(Math.min(...timestamps)).toISOString();
          const max = new Date(Math.max(...timestamps)).toISOString();
          return { min, max };
        };

        const applyCycleFilter = <T extends { date_received?: string | null; date_submission?: string | null }>(
          rows: T[],
          source: 'enroll' | 'fallback'
        ): T[] => {
          if (!uploadCycleStartDate) return rows;
          const cycleStart = Date.parse(uploadCycleStartDate);
          if (Number.isNaN(cycleStart)) return rows;

          const before = summarizeDates(rows);
          const filtered = rows.filter((r) => {
            const rawDate = r.date_received || r.date_submission;
            if (!rawDate) return false;
            const filingTs = Date.parse(rawDate);
            return !Number.isNaN(filingTs) && filingTs >= cycleStart;
          });
          const after = summarizeDates(filtered);
          console.info(
            `[proceedings-upload] cycle filter (${source}) docket=${docketNumber} cycleStart=${uploadCycleStartDate} before=${rows.length} after=${filtered.length} beforeMin=${before.min ?? 'n/a'} beforeMax=${before.max ?? 'n/a'} afterMin=${after.min ?? 'n/a'} afterMax=${after.max ?? 'n/a'}`
          );
          return filtered;
        };

        const enrollUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/enroll-docket`;
        const res = await fetch(enrollUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ docketNumber }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.warn(`[proceedings-upload] enroll-docket not ok for ${docketNumber}:`, res.status, text);
          Sentry.addBreadcrumb({
            category: 'edge-fn',
            level: 'warning',
            message: 'enroll-docket non-2xx',
            data: { docketNumber, status: res.status },
          });
          setUploadStatusNote(`Upload skipped: enroll request failed (${res.status}).`);
          return;
        }
        const data = await res.json();
        const enrollFilings: MinimalFiling[] = Array.isArray(data.filings) ? data.filings : [];
        console.info(
          `[proceedings-upload] enroll response for ${docketNumber}: total_filings=${enrollFilings.length}, alreadyEnrolled=${!!data?.alreadyEnrolled}`
        );

        // Non-express filings that have documents to upload.
        let uploadable = enrollFilings.filter(
          (f) => !(f.express_comment === 1 || f.express_comment === true)
        );
        uploadable = applyCycleFilter(uploadable, 'enroll');

        // Deterministic fallback: if enroll returns no uploadable filings, source candidates
        // from filings retrieval so storage repair still runs after folder deletion.
        if (uploadable.length === 0) {
          if (enrollFilings.length === 0) {
            console.info(`[proceedings-upload] enroll returned 0 filings for ${docketNumber}; attempting fallback candidates`);
          } else {
            console.info(
              `[proceedings-upload] uploadable=0 after express filter for ${docketNumber}; attempting fallback candidates`
            );
          }

          try {
            const fallback = await fetchProceedingsFilings(docketNumber, null, 200, {
              allowBroadFallback: true,
            });
            uploadable = fallback.filings.map((f) => ({
              id_submission: f.id_submission,
              filers: f.filers,
              submissiontype: f.submissiontype,
              filing_type: f.filing_type ?? null,
              express_comment: f.express_comment,
              date_received: f.date_received,
              date_submission: f.date_submission,
            }));
            uploadable = applyCycleFilter(uploadable, 'fallback');

            console.info(
              `[proceedings-upload] fallback candidates for ${docketNumber}: total=${fallback.filings.length}, mode=${fallback.meta.sourceMode}, fallbackUsed=${fallback.meta.fallbackUsed}`
            );
          } catch (fallbackErr) {
            console.warn(`[proceedings-upload] fallback candidate query failed for ${docketNumber}:`, fallbackErr);
          }
        }

        if (uploadable.length === 0) {
          const nonExpressCount = enrollFilings.filter(
            (f) => !(f.express_comment === 1 || f.express_comment === true)
          ).length;
          setUploadStatusNote(
            `Upload pass skipped: no non-express candidates (enroll filings: ${enrollFilings.length}, non-express: ${nonExpressCount}).`
          );
          return;
        }

        setUploadProgress({ current: 0, total: uploadable.length });
        setUploadStatusNote(`Checking ${uploadable.length} filing document sets...`);

        await uploadFilingsParallel(uploadable, docketNumber, (done, total) => {
          setUploadProgress({ current: done, total });
        });

        setUploadProgress(null);
        setUploadStatusNote(null);
        // Mark as checked so navigating back to this page doesn't re-run the pass
        sessionStorage.setItem(sessionKey, '1');
        // Persist completion server-side so other sessions/devices skip the pass too.
        try {
          await supabase.rpc('mark_docket_documents_verified', {
            p_docket_number: docketNumber,
          });
        } catch (markErr) {
          console.warn('[proceedings-upload] mark_docket_documents_verified failed:', markErr);
        }
        // Pre-warm the NPRM questions cache now that all filings are in storage.
        prewarmNprmCache();
      } catch (err) {
        console.error('Auto-enroll/upload error:', err);
        setUploadProgress(null);
        enrolledRef.current = null;
        setUploadStatusNote(
          `Upload pass failed: ${err instanceof Error ? err.message : 'unknown error'}.`
        );
      }
    })();
  }, [
    docketNumber,
    docketDataResolved,
    nprmCount,
    publicNoticeCount,
    uploadCycleStartDate,
  ]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      const now = new Date();
      if (date > now && date.getFullYear() > now.getFullYear() + 1) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', dateString, err);
      return 'N/A';
    }
  };

  const getProcessingStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'processing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'paused':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getProcessingStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Loaded';
      case 'processing':
        return 'Loading';
      case 'pending':
        return 'Not loaded';
      case 'failed':
        return 'Failed';
      case 'paused':
        return 'Paused';
      default:
        return status || 'Not loaded';
    }
  };

  const getProcessingStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'processing':
        return <Clock className="w-5 h-5" />;
      case 'failed':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  // Prefer the non-express count everywhere we surface a "filings" number.
  // Falls back to the raw ECFS count so stale caches / missing field still render something.
  const displayFilingCount = nonExpressFilingCount ?? fileCount;
  // Public (express) comments = authoritative total − substantive filings.
  const publicCommentCount =
    fileCount !== null && nonExpressFilingCount !== null
      ? Math.max(0, fileCount - nonExpressFilingCount)
      : null;
  // Docket-fitness: CommsGenie targets recent comment cycles on specific
  // proceedings — not massive long-running dockets or viral public-comment
  // campaigns. Flag poor-fit dockets so users know coverage may be partial.
  const docketFitness: 'good' | 'viral' | 'mega' | null =
    fileCount == null
      ? null
      : publicCommentCount != null && publicCommentCount > 5000
        ? 'viral'
        : fileCount > 20000
          ? 'mega'
          : 'good';

  const tabs = useMemo(() => {
    const base: Array<{
      id: ProceedingTab;
      label: string;
      icon: React.ReactNode;
      count?: string;
    }> = [
      {
        id: 'overview',
        label: 'Overview',
        icon: <ListTree className="w-4 h-4" />,
      },
    ];
    if (prevailingInstrument !== 'public_notice') {
      base.push({
        id: 'nprm_questions',
        label: 'NPRM Questions',
        icon: <NotebookTabs className="w-4 h-4" />,
      });
    } else {
      base.push({
        id: 'public_notice_questions',
        label: 'Public Notice Questions',
        icon: <NotebookTabs className="w-4 h-4" />,
      });
    }
    base.push(
      {
        id: 'filings',
        label: 'Filings',
        icon: <FileText className="w-4 h-4" />,
        count: displayFilingCount !== null ? displayFilingCount.toLocaleString() : undefined,
      },
      {
        id: 'comment_summaries',
        label: 'Comment Summaries',
        icon: <MessageSquareText className="w-4 h-4" />,
      },
      {
        id: 'draft_reply',
        label: 'Substantiate',
        icon: <PenLine className="w-4 h-4" />,
      },
      {
        id: 'order',
        label: 'The Order',
        icon: <Gavel className="w-4 h-4" />,
      }
      // Comment Chat tab intentionally hidden — feature still in development.
      // To restore, re-add { id: 'chat', label: 'Comment Chat', icon: <Bot/> }
      // and remove this comment. The underlying CommentArtifactChat component
      // and its API wiring are still shipped so unhiding is a one-line change.
    );
    return base;
  }, [prevailingInstrument, displayFilingCount]);

  useEffect(() => {
    if (prevailingInstrument === 'public_notice' && activeTab === 'nprm_questions') {
      handleTabChange('overview');
    }
    if (prevailingInstrument !== 'public_notice' && activeTab === 'public_notice_questions') {
      handleTabChange('overview');
    }
  }, [prevailingInstrument, activeTab, handleTabChange]);

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 text-green-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading proceeding details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !proceeding) {
    return (
      <div className="p-4 sm:p-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="mb-6">
            <button
              onClick={() => navigate('/proceedings')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Proceedings</span>
            </button>
          </div>
          
          {(error || '').toLowerCase().includes('not found') ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
              <h3 className="text-gray-900 font-semibold text-lg mb-1">We couldn&rsquo;t find that docket</h3>
              <p className="text-gray-500 mb-5">
                No FCC proceeding matches{' '}
                <span className="font-mono text-gray-700">{docketNumber}</span>. Double-check the
                number, or browse the open proceedings.
              </p>
              <button
                onClick={() => navigate('/proceedings')}
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-white text-sm font-medium hover:bg-green-700"
              >
                Browse open proceedings
              </button>
            </div>
          ) : (
            <div className="backdrop-blur-xl bg-red-50 border border-red-300 rounded-lg p-6 shadow-sm">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-red-800 font-semibold mb-2 text-lg">Error</h3>
                  <p className="text-red-700">{error || 'Proceeding not found'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ProceedingDetailHeader
        docketNumber={proceeding.docket_number}
        proceedingName={proceeding.proceeding_name || ''}
        status={proceeding.status}
        lastProcessedAt={proceeding.last_processed_at}
        onBack={() => navigate('/proceedings')}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
        fileCount={displayFilingCount}
        publicCommentCount={publicCommentCount}
        nprmCount={nprmCount}
        publicNoticeCount={publicNoticeCount}
        prevailingInstrument={prevailingInstrument}
        fetchingFilingCount={fetchingFilingCount}
        fetchingNprmCount={fetchingNprmCount}
        commentDeadline={commentDeadline}
        replyCommentDeadline={replyCommentDeadline}
        commentDeadlineExtension={commentDeadlineExtension}
        replyDeadlineExtension={replyDeadlineExtension}
        fetchingDeadlines={fetchingDeadlines}
        formatShortDate={formatDate}
        formatRegulatoryShortDate={(iso) => formatDeadlineCalendarDate(iso)}
      />

      <div
        className="w-full px-4 py-8 sm:px-9"
        role="tabpanel"
        id="proceeding-tabpanel"
        aria-labelledby={`proceeding-tab-${activeTab}`}
      >
        {docketFitness && docketFitness !== 'good' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <div>
              {docketFitness === 'viral' ? (
                <>
                  This proceeding drew a large public-comment campaign (
                  {publicCommentCount?.toLocaleString()} comments). CommsGenie focuses on the{' '}
                  <strong>substantive filings and recent activity</strong> — the individual public
                  comments aren&rsquo;t summarized.
                </>
              ) : (
                <>
                  This is a large, long-running docket. CommsGenie is built for{' '}
                  <strong>recent comment cycles on specific proceedings</strong>, so coverage here
                  may be partial.
                </>
              )}
            </div>
          </div>
        )}
        {isAdmin && uploadProgress && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 mb-4 text-sm flex items-center gap-2">
            <Loader className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-blue-700">
              Uploading PDFs: {uploadProgress.current} / {uploadProgress.total}
            </span>
          </div>
        )}
        {isAdmin && !uploadProgress && uploadStatusNote && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 text-sm text-slate-700">
            {uploadStatusNote}
          </div>
        )}

        {activeTab === 'overview' && (
          <>
            <Link
              to={`/comment-summaries/${proceeding.docket_number}`}
              className="group mb-6 flex items-center justify-between gap-4 rounded-xl border border-cg-green/30 bg-gradient-to-r from-emerald-50/80 to-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cg-green/15 text-cg-green">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-base font-semibold text-cg-ink">Summarize comments on this docket</div>
                  <div className="mt-0.5 text-xs text-gray-600">
                    Every filing, page-cited and topic-tagged, in under three minutes.
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5 rounded-lg bg-cg-nav px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors group-hover:bg-cg-green">
                Generate summary
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
            <Link
              to={`/proceedings/${proceeding.docket_number}/digest`}
              className="group mb-6 flex items-center justify-between gap-4 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50/80 to-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                  <Compass className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-base font-semibold text-cg-ink">See the comment digest</div>
                  <div className="mt-0.5 text-xs text-gray-600">
                    The key issues, who&apos;s aligned, and a topic map — a 2-minute orientation.
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition-colors group-hover:bg-blue-50">
                View digest
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
            <div className="mb-6 grid gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-stone-200/80 bg-white p-6 shadow-sm lg:col-span-2">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">At a glance</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Key deadlines and filing context for this proceeding.
                    </p>
                  </div>
                  {/* Only surface processing state when it's actionable — a
                      user doesn't need a "Loaded" badge on every healthy docket
                      (that's internal ingest vocabulary). Show it only while
                      data is still updating or if ingest failed. */}
                  {(proceeding.processing_status === 'processing' ||
                    proceeding.processing_status === 'failed') && (
                    <div
                      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${getProcessingStatusColor(proceeding.processing_status)}`}
                    >
                      {getProcessingStatusIcon(proceeding.processing_status)}
                      <span className="text-sm font-medium">
                        {proceeding.processing_status === 'processing' ? 'Updating…' : 'Data may be incomplete'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {proceeding.bureau && (
                    <div className="flex gap-3 rounded-lg border border-stone-100 bg-stone-50/80 p-4">
                      <FolderOpen className="h-5 w-5 shrink-0 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Bureau</p>
                        <p className="mt-0.5 font-medium text-gray-900">{proceeding.bureau}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 rounded-lg border border-stone-100 bg-stone-50/80 p-4">
                    <Calendar className="h-5 w-5 shrink-0 text-gray-400" />
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Created</p>
                      <p className="mt-0.5 font-medium text-gray-900">{formatDate(proceeding.created_at)}</p>
                    </div>
                  </div>
                  {fetchingDeadlines ? (
                    <div className="flex items-center gap-2 rounded-lg border border-stone-100 bg-stone-50/80 p-4 sm:col-span-2">
                      <Loader className="h-4 w-4 animate-spin text-gray-400" />
                      <span className="text-sm text-gray-500">Loading deadlines…</span>
                    </div>
                  ) : (
                    <>
                      {commentDeadline && (
                        <div className="flex gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
                          <Calendar className="h-5 w-5 shrink-0 text-emerald-600" />
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700/80">
                              Comments due
                            </p>
                            <p className="mt-0.5 font-semibold text-gray-900">
                              {formatDeadlineCalendarDate(commentDeadline)}
                            </p>
                          </div>
                        </div>
                      )}
                      {replyCommentDeadline && (
                        <div className="flex gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
                          <Calendar className="h-5 w-5 shrink-0 text-emerald-600" />
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700/80">
                              Reply comments due
                            </p>
                            <p className="mt-0.5 font-semibold text-gray-900">
                              {formatDeadlineCalendarDate(replyCommentDeadline)}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-stone-200/80 bg-white p-6 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  {prevailingInstrument === 'public_notice' ? 'Public notice' : 'NPRM activity'}
                </p>
                {fetchingNprmCount ? (
                  <div className="mt-4 flex items-center gap-2 text-gray-500">
                    <Loader className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Loading…</span>
                  </div>
                ) : prevailingInstrument === 'public_notice' ? (
                  <>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
                      {publicNoticeCount !== null ? publicNoticeCount.toLocaleString() : '—'}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">Bureau or Commission public notice</p>
                    {publicNoticeDates.length > 0 && (
                      <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar className="h-3.5 w-3.5" />
                        {publicNoticeDates.length === 1 ? 'Released' : 'Most recent'}:{' '}
                        {formatDate(publicNoticeDates[0])}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
                      {nprmCount !== null ? nprmCount.toLocaleString() : '—'}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">Notices of proposed rulemaking</p>
                    {nprmDates.length > 0 && (
                      <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar className="h-3.5 w-3.5" />
                        {nprmDates.length === 1 ? 'Published' : 'Most recent'}: {formatDate(nprmDates[0])}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {prevailingInstrument === 'nprm' && nprmCount !== null && nprmCount > 0 ? (
              <TimelineSection
                docketNumber={proceeding.docket_number}
                proceedingName={proceeding.proceeding_name}
                resolvedCommentDeadline={commentDeadline}
                resolvedReplyCommentDeadline={replyCommentDeadline}
              />
            ) : prevailingInstrument === 'public_notice' ? (
              <div className="rounded-xl border border-stone-200/80 bg-white p-6 text-sm text-gray-600 shadow-sm">
                <p className="font-medium text-gray-900">Public notice proceeding</p>
                <p className="mt-2">
                  Comment and reply windows follow the Bureau public notice. Use the Filings tab to
                  browse filings by comment period.
                </p>
                {topPublicNoticePdfUrl ? (
                  <p className="mt-3">
                    <a
                      href={topPublicNoticePdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 underline decoration-emerald-700/30 hover:text-emerald-800"
                    >
                      Open public notice document (PDF)
                    </a>
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="rounded-xl border border-stone-200/80 bg-white p-6 text-sm text-gray-600 shadow-sm">
                NPRM timeline is not available for this docket yet.
              </div>
            )}
          </>
        )}

        {activeTab === 'nprm_questions' && (
          <NPRMPreviewSection
            docketNumber={proceeding.docket_number}
            nprmCount={nprmCount}
            proceedingName={proceeding.proceeding_name}
            questionsOnly
          />
        )}

        {activeTab === 'public_notice_questions' && (
          <PublicNoticePreviewSection
            docketNumber={proceeding.docket_number}
            publicNoticeCount={publicNoticeCount}
            proceedingName={proceeding.proceeding_name}
            publicNoticeDocument={publicNoticeDocuments[0] ?? null}
          />
        )}

        {activeTab === 'filings' && (
          <FilingsSection
            docketNumber={proceeding.docket_number}
            authoritativeCount={nonExpressFilingCount}
            syntheticTimelineForFilings={syntheticPnTimelineForFilings}
            onProcessForSummary={() => {
              // No-op on this page-level view; summaries manage their own data fetch.
            }}
          />
        )}

        {/* SummariesSection mounts on tab activation rather than page load.
            Prefetching its timeline + filings + spam-filer batch check + NPRM
            storage listings on every proceedings-page visit added meaningful
            latency for users who never opened this tab. Trade-off accepted:
            a small spinner on first tab open. */}
        {proceeding && activeTab === 'comment_summaries' && (
          <>
            <SummariesSection
              docketNumber={proceeding.docket_number}
              commentDeadline={commentDeadline}
              replyCommentDeadline={replyCommentDeadline}
              nprmCount={nprmCount}
              fetchingNprmCount={fetchingNprmCount}
              prevailingInstrument={prevailingInstrument}
              syntheticTimelineForFilings={syntheticPnTimelineForFilings}
              publicNoticeCount={publicNoticeCount}
              topPublicNoticeDocument={publicNoticeDocuments[0] ?? null}
            />
            {/* Handoff to the dedicated drafting tab — the natural next step
                after reading the record: "now draft your reply." Same
                destination as the top-level "Draft a Reply" tab. */}
            <button
              onClick={() => navigate(`/proceedings/${proceeding.docket_number}/reply`)}
              className="mt-6 w-full flex items-center gap-3 rounded-xl border border-cg-border bg-white p-4 text-left hover:border-cg-green transition-colors"
            >
              <PenLine className="w-5 h-5 text-cg-green shrink-0" />
              <span>
                <b className="block text-[15px]">Ready to respond? Substantiate your position →</b>
                <span className="text-[13px] text-slate-500">
                  Give a few bullets; we ground them in what others filed. Prosumer &amp; Pro.
                </span>
              </span>
            </button>
          </>
        )}

        {/* Reply-comment drafting — a launcher into the full-page drafting
            workspace (its own route; the working window needs the room). */}
        {proceeding && activeTab === 'draft_reply' && (
          <div className="rounded-xl border border-cg-border bg-white p-6 max-w-2xl">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <PenLine className="w-5 h-5 text-cg-green" /> Substantiate your position
              <span className="rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-amber-200">Beta</span>
            </h2>
            <p className="mt-2 text-slate-600 text-[15px]">
              Give a few bullets of what you want to argue. We substantiate them against what
              others actually filed in this docket — engaging the real commenters, with citations
              — and hand you a cited draft to refine and export. You supply the facts; we ground
              the argument.
            </p>
            <button
              onClick={() => navigate(`/proceedings/${proceeding.docket_number}/reply`)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-cg-green px-4 py-2.5 text-white text-sm font-medium"
            >
              <PenLine className="w-4 h-4" /> Substantiate your position →
            </button>
            <p className="mt-2 text-[12px] text-slate-400">Prosumer &amp; Pro. A drafting aid, not legal advice.</p>
          </div>
        )}

        {/* The Order — docket-keyed, shared R&O analysis (obligations,
            deadlines, what-changed). Mounts on tab activation; the
            backend caches the analysis per-docket so repeat views are
            cheap. Shows a "no order yet" state for proceedings that
            haven't reached a final order. */}
        {proceeding && activeTab === 'order' && (
          <OrderSection docketNumber={proceeding.docket_number} />
        )}

        {/* Comment Chat — typed-artifact-backed (replaces legacy RAG chat).
            Mounts on tab activation rather than page load: the chat fires
            POST /refresh on mount which is cheap when nothing changed but
            we don't want to fire it on every proceedings-page visit when
            the user isn't going to use the tab. */}
        {proceeding && activeTab === 'chat' && (
          <ChatSection docketNumber={proceeding.docket_number} />
        )}
      </div>
    </div>
  );
};

